import React, { Children, isValidElement, useMemo, useState, useCallback, useEffect, createContext, useContext } from 'react';
import styled from 'styled-components';
import { useResponsiveV2 } from '../../hooks/use-responsive-v2.hook';
import { getColor } from '../../hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var LayoutContext = /*#__PURE__*/createContext({
  hasSider: false,
  hasHeader: false,
  hasIcon: false,
  collapsed: false,
  toggleCollapsed: function toggleCollapsed() {},
  setCollapsed: function setCollapsed() {}
});
export var useLayoutContext = function useLayoutContext() {
  return useContext(LayoutContext);
}; // Layout Component

var StyledLayout = styled.div.withConfig({
  displayName: "layout__StyledLayout",
  componentId: "sc-pqd1xk-0"
})(["display:flex;flex-direction:column;height:100vh;width:100%;overflow:hidden;position:fixed;top:0;left:0;right:0;bottom:0;background-color:", ";"], getColor('fillHighlight3'));
var BodyWrapper = styled.div.withConfig({
  displayName: "layout__BodyWrapper",
  componentId: "sc-pqd1xk-1"
})(["display:flex;flex:1 1 auto;flex-direction:", ";min-height:0;overflow:hidden;"], function (props) {
  return props.$hasSider ? 'row' : 'column';
});
var IconLayoutWrapper = styled.div.withConfig({
  displayName: "layout__IconLayoutWrapper",
  componentId: "sc-pqd1xk-2"
})(["display:grid;grid-template-columns:auto 1fr;grid-template-rows:auto 1fr;height:100%;width:100%;"]);
var IconHeaderRow = styled.div.withConfig({
  displayName: "layout__IconHeaderRow",
  componentId: "sc-pqd1xk-3"
})(["display:flex;align-items:stretch;width:100%;"]);
var IconHeaderSlot = styled.div.withConfig({
  displayName: "layout__IconHeaderSlot",
  componentId: "sc-pqd1xk-4"
})(["flex:1;min-width:0;"]);
var IconSlot = styled.div.withConfig({
  displayName: "layout__IconSlot",
  componentId: "sc-pqd1xk-5"
})(["grid-column:1 / 2;grid-row:1 / 2;"]);
var HeaderSlot = styled.div.withConfig({
  displayName: "layout__HeaderSlot",
  componentId: "sc-pqd1xk-6"
})(["grid-column:2 / 3;grid-row:1 / 2;"]);
var SiderSlot = styled.div.withConfig({
  displayName: "layout__SiderSlot",
  componentId: "sc-pqd1xk-7"
})(["grid-column:1 / 2;grid-row:2 / 3;"]);
var ContentSlot = styled.div.withConfig({
  displayName: "layout__ContentSlot",
  componentId: "sc-pqd1xk-8"
})(["grid-column:2 / 3;grid-row:2 / 3;display:flex;flex-direction:column;min-height:0;overflow:hidden;"]);
var COLLAPSED_STORAGE_KEY = 'layout-sidebar-collapsed';
export var Layout = function Layout(_ref) {
  var children = _ref.children;

  var _useState = useState(function () {
    if (true) {
      var stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    }

    return false;
  }),
      collapsed = _useState[0],
      setCollapsedState = _useState[1];

  var _useResponsiveV = useResponsiveV2(),
      isMobile = _useResponsiveV.isMobile;

  var childArray = Children.toArray(children);
  var header = childArray.find(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutHeader';
  });
  var footer = childArray.find(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutFooter';
  });
  var hasSider = childArray.some(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutSider';
  });
  var hasHeader = childArray.some(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutHeader';
  });
  var icon = childArray.find(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutIcon';
  });
  var sider = childArray.find(function (child) {
    return /*#__PURE__*/isValidElement(child) && child.type && child.type.displayName === 'LayoutSider';
  });
  var bodyChildren = childArray.filter(function (child) {
    return !( /*#__PURE__*/isValidElement(child) && child.type && (child.type.displayName === 'LayoutHeader' || child.type.displayName === 'LayoutFooter' || child.type.displayName === 'LayoutIcon' || child.type.displayName === 'LayoutSider'));
  });
  useEffect(function () {
    if (isMobile && hasSider) {
      setCollapsedState(true);
    }
  }, [isMobile, hasSider]);
  var toggleCollapsed = useCallback(function () {
    setCollapsedState(function (prev) {
      var newValue = !prev;

      if (true) {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(newValue));
      }

      return newValue;
    });
  }, []);
  var setCollapsed = useCallback(function (value) {
    setCollapsedState(value);

    if (true) {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(value));
    }
  }, []);
  var hasIcon = !!icon;
  var contextValue = useMemo(function () {
    return {
      hasSider: hasSider,
      hasHeader: hasHeader,
      hasIcon: hasIcon,
      collapsed: collapsed,
      toggleCollapsed: toggleCollapsed,
      setCollapsed: setCollapsed
    };
  }, [hasSider, hasHeader, hasIcon, collapsed, toggleCollapsed, setCollapsed]);
  return /*#__PURE__*/_jsx(LayoutContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(StyledLayout, {
      children: icon && sider ? /*#__PURE__*/_jsxs(IconLayoutWrapper, {
        children: [/*#__PURE__*/_jsx(IconSlot, {
          children: icon
        }), header && /*#__PURE__*/_jsx(HeaderSlot, {
          children: header
        }), /*#__PURE__*/_jsx(SiderSlot, {
          children: sider
        }), /*#__PURE__*/_jsxs(ContentSlot, {
          children: [bodyChildren, footer]
        })]
      }) : icon ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(IconHeaderRow, {
          children: [icon, header && /*#__PURE__*/_jsx(IconHeaderSlot, {
            children: header
          })]
        }), /*#__PURE__*/_jsx(BodyWrapper, {
          $hasSider: false,
          children: bodyChildren
        }), footer]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [header, /*#__PURE__*/_jsx(BodyWrapper, {
          $hasSider: hasSider,
          children: bodyChildren
        }), footer]
      })
    })
  });
};
Layout.displayName = 'Layout';